import { FilterConfig, FilterType } from '@/components/common/FilterableDataTable/TableHeader';
import { QUALITY_SCORE_LEVELS, QUALITY_SCORE_LABELS, QUALITY_SCORE_THRESHOLDS } from '@/constants';
import { mapCallEndingReasonToLabel } from '../CallDetails/callDetailsUtils';
import { TEST_DATE_RANGE_OPTIONS } from '@/testFeatures';
import type { CallAgents } from '@/hooks/useFetchCalls';

const QUALITY_SCORE_OPTION_COLORS: Record<string, string> = {
    [QUALITY_SCORE_LEVELS.GOOD]: 'success.light',
    [QUALITY_SCORE_LEVELS.NEUTRAL]: 'warning.light',
    [QUALITY_SCORE_LEVELS.NEEDS_ATTENTION]: 'error.light',
};

/**
 * Generates quality score filter options with dynamic labels and color tokens based on thresholds.
 */
const getQualityScoreOptions = () => {
    const { GOOD, NEUTRAL, NEEDS_ATTENTION } = QUALITY_SCORE_LEVELS;
    const { GOOD_MIN, NEUTRAL_MIN } = QUALITY_SCORE_THRESHOLDS;

    return [
        { value: GOOD, label: `${QUALITY_SCORE_LABELS[GOOD]} (${GOOD_MIN}-100%)`, backgroundColor: QUALITY_SCORE_OPTION_COLORS[GOOD] },
        {
            value: NEUTRAL,
            label: `${QUALITY_SCORE_LABELS[NEUTRAL]} (${NEUTRAL_MIN}-${GOOD_MIN}%)`,
            backgroundColor: QUALITY_SCORE_OPTION_COLORS[NEUTRAL],
        },
        {
            value: NEEDS_ATTENTION,
            label: `${QUALITY_SCORE_LABELS[NEEDS_ATTENTION]} (<${NEUTRAL_MIN}%)`,
            backgroundColor: QUALITY_SCORE_OPTION_COLORS[NEEDS_ATTENTION],
        },
    ];
};

const callListFilters: FilterConfig[] = [
    {
        id: 'date_range',
        label: 'Date range',
        type: FilterType.SELECT,
        columnId: 'created_at',
        hideAllOption: true,
        permanent: true,
        options: [
            { value: 'today', label: 'Today' },
            { value: 'yesterday', label: 'Yesterday' },
            { value: 'last_7_days', label: 'Last 7 days' },
            { value: 'last_30_days', label: 'Last 30 days' },
            { value: 'last_60_days', label: 'Last 60 days' },
            { value: 'last_90_days', label: 'Last 90 days' },

            ...TEST_DATE_RANGE_OPTIONS,
        ],
    },
    {
        id: 'agent_name',
        label: 'Agent name',
        type: FilterType.SELECT,
        columnId: 'agent_id',
        options: [],
        maxWidth: '300px',
    },
    {
        id: 'call_duration',
        label: 'Call duration',
        type: FilterType.SELECT,
        columnId: 'talk_time',
        options: [
            { value: '20', label: '20 seconds or longer' },
            { value: '120', label: '2 minutes or longer' },
            { value: '300', label: '5 minutes or longer' },
            { value: '600', label: '10 minutes or longer' },
        ],
    },
    {
        id: 'direction',
        label: 'Direction',
        type: FilterType.SELECT,
        columnId: 'direction',
        options: [
            { value: 'in', label: 'Inbound' },
            { value: 'out', label: 'Outbound' },
        ],
    },
    {
        id: 'quality_level',
        label: 'Quality score',
        type: FilterType.SELECT,
        columnId: 'quality_score',
        options: getQualityScoreOptions(),
    },
    {
        id: 'reason_name',
        label: 'Outcome',
        type: FilterType.SELECT,
        columnId: 'reason_name',
        options: [],
    },
];

const createCallListFilters = (agents: CallAgents[] = [], callEndReasons: string[] | null): FilterConfig[] => {
    // Create agent options from the agents returned with calls data
    const agentNameOptions = agents.map(agent => ({
        value: agent.id.toString(),
        label: agent.name,
    }));

    // Create outcome options from the callEndReasons data
    const outcomeOptions = callEndReasons?.map(reason => ({
        value: reason,
        label: mapCallEndingReasonToLabel(reason || null) || '',
    }));

    return callListFilters.map((filter): FilterConfig => {
        if (filter.id === 'agent_name') {
            return { ...filter, options: agentNameOptions };
        }

        if (filter.id === 'reason_name') {
            return { ...filter, options: outcomeOptions };
        }
        return filter;
    });
};

export default createCallListFilters;
